/**
 * Otto Agent composer — the command dialog where the staff user types what they
 * want ("Draft an invoice for Acme…") and Otto takes over via the cloak. There
 * is NO standalone launcher button: the composer is opened from inside the Otto
 * Staff chat widget (its sparkle button → `otto:agent-run` bridge), from the
 * "Create with AI" buttons on the invoice/quote/campaign pages, and from ⌘K.
 * Only active when the user has at least one permitted skill.
 */
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { useEffect, useState, type JSX } from 'react';

import { OTTO_SKILL_LABELS, type OttoAgentSkillId } from '@xop/dashboard-shared';

import { useAgentic } from './AgenticProvider.js';
import { useAgenticEnabled } from './useAgenticEnabled.js';
import { MicButton } from './MicButton.js';

/** Example prompts seeded into the input when a skill chip is clicked. Use REAL
 * catalog products (e.g. Mass Notification) so Otto never chases a name that
 * doesn't exist. */
const SKILL_EXAMPLES: Record<OttoAgentSkillId, string> = {
  create_invoice: 'Draft an invoice for Acme Corp — 10× Mass Notification at $25, 10% discount',
  create_quote: 'Draft a quote for Acme Corp — 10× Mass Notification at $25 each',
  create_campaign: 'Draft an email campaign to the Newsletter group about our latest release',
};

export function OttoAgentLauncher(): JSX.Element | null {
  const { enabled, skills } = useAgenticEnabled();
  const { start, active, composerOpen, composerSkill, openComposer, closeComposer } = useAgentic();
  const [text, setText] = useState('');

  // ⌘K / Ctrl-K opens the command dialog (unless the cloak is already up).
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!active) openComposer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, active, openComposer]);

  // Seed the input when the composer opens: pre-scoped (a "Create with AI"
  // button) → that skill's example; otherwise blank.
  useEffect(() => {
    if (composerOpen) setText(composerSkill ? SKILL_EXAMPLES[composerSkill] : '');
  }, [composerOpen, composerSkill]);

  if (!enabled) return null;

  const submit = () => {
    const prompt = text.trim();
    if (!prompt) return;
    closeComposer();
    setText('');
    start(prompt);
  };

  return (
    <>
      <Dialog
        open={composerOpen}
        onClose={closeComposer}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AutoAwesomeRoundedIcon color="primary" fontSize="small" />
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
              {composerSkill ? `${OTTO_SKILL_LABELS[composerSkill]} with Otto` : 'Ask Otto to do it'}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            Describe the invoice, quote or campaign you want. Otto gathers the details and opens the
            form pre-filled — you review and save.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="e.g. Draft an invoice for Acme Corp: 10 units of Mass Notification at $25 with a 10% discount"
            inputProps={{ 'data-testid': 'otto-agent-input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.75 }}>
                  <MicButton onText={(t) => setText((p) => (p.trim() ? `${p.trim()} ` : '') + t)} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            {skills.map((s) => (
              <Chip
                key={s}
                label={OTTO_SKILL_LABELS[s]}
                variant="outlined"
                size="small"
                onClick={() => setText(SKILL_EXAMPLES[s])}
                sx={{
                  borderColor: 'divider',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) },
                }}
              />
            ))}
          </Stack>
          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Chip
              label="Ask Otto"
              color="primary"
              onClick={submit}
              disabled={!text.trim()}
              data-testid="otto-agent-submit"
              sx={{ px: 1.5, fontWeight: 600, cursor: text.trim() ? 'pointer' : 'default' }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
